import { Outlet, redirect, type RouteObject } from "react-router-dom";
import { ADMIN_ROUTES } from "../admin/AdminRoutes";
import { loginCallbackLoader } from "../auth/LoginCallbackLoader";
import { NotFoundPage } from "../shared/screens/ErrorScreen";
import { StaticLoadingScreen } from "../shared/screens/LoadingScreen";
import { AppPage } from "./AppPage";
import { getRelativePath, PAGE_TWO_PREFIX } from "./AppRoutes";
import { reportLoader } from "../report/reportLoader";
import { AdminLoadingPage } from "../admin/pages/AdminLoadingPage";
import { LoadingRouteScreen } from "./PageShell";
import { adminRouteLoader } from "../admin/auth/adminRouteLoader";

const adminRoutes: RouteObject = {
  path: "admin",
  lazy: async () => {
    const { AdminWrapper } = await import("../admin/AdminWrapper");
    return { Component: AdminWrapper };
  },
  HydrateFallback: AdminLoadingPage,
  children: [
    {
      path: "login",
      lazy: async () => {
        const { default: AdminLogin } = await import(
          "../admin/auth/AdminLogin"
        );
        return { Component: AdminLogin };
      },
    },
    {
      loader: adminRouteLoader,
      lazy: async () => {
        const { default: AdminProtectedRoutes } = await import(
          "../admin/AdminProtectedRoutes"
        );
        return { Component: AdminProtectedRoutes };
      },
      children: Object.values(ADMIN_ROUTES).map(({ path, component }) => ({
        path,
        lazy: async () => ({ Component: (await component()).default }),
      })),
    },
  ],
};

export const routes: RouteObject[] = [
  adminRoutes,
  {
    lazy: async () => {
      const { PageShell } = await import("./PageShell");
      return { Component: PageShell };
    },

    //Component: LoadingRouteScreen,
    HydrateFallback: LoadingRouteScreen,
    children: [
      {
        path: "/t/:token",
        children: [
          {
            path: ":path",
            lazy: async () => {
              const { ShortReportUrlRoute } = await import(
                "./ShortReportUrlRoute"
              );
              return { Component: ShortReportUrlRoute };
            },
          },
          {
            index: true,
            lazy: async () => {
              const { ShortReportUrlRoute } = await import(
                "./ShortReportUrlRoute"
              );
              return { Component: ShortReportUrlRoute };
            },
          },
        ],
      },
      {
        path: "/i/:token",
        children: [
          {
            path: ":path",
            lazy: async () => {
              const { ShortInvitationRoute } = await import(
                "./ShortInvitationRoute"
              );
              return { Component: ShortInvitationRoute };
            },
          },
          {
            index: true,
            lazy: async () => {
              const { ShortInvitationRoute } = await import(
                "./ShortInvitationRoute"
              );
              return { Component: ShortInvitationRoute };
            },
          },
        ],
      },
      {
        path: getRelativePath(AppPage.Create),
        lazy: async () => {
          const { CreateNewReportPage } = await import(
            "../report/landing/create/CreateNewReportPage"
          );
          return { Component: CreateNewReportPage };
        },
      },
      {
        path: "stengt",
        children: [
          {
            path: getRelativePath(AppPage.Split),
            lazy: async () => {
              const { SplitReportPage } = await import(
                "../report/locked-report/SplitReportPage"
              );
              return { Component: SplitReportPage };
            },
          },
          {
            index: true,
            loader: (x) => {
              // This redirect only works when run in the cloud, in localhost it returns an error.
              // the Split page checks for token param and shows NotFoundPage if not present
              const nextPath = getRelativePath(AppPage.Split);
              const nextSearch = new URL(x.request.url).searchParams.toString();
              const nextLocation = `${nextPath}?${nextSearch}`;
              return redirect(nextLocation);
            },
          },
        ],
      },
      {
        path: getRelativePath(AppPage.AboutLandingPage),
        children: [
          {
            path: getRelativePath(AppPage.AboutDigitalSigning),
            lazy: async () => {
              const { DigitalSigningPage } = await import(
                "../about/DigitalSigningPage"
              );
              return { Component: DigitalSigningPage };
            },
          },
          {
            path: getRelativePath(AppPage.AboutFaq),
            lazy: async () => {
              const { FaqPage } = await import("../about/FaqPage");
              return { Component: FaqPage };
            },
          },
          {
            path: getRelativePath(AppPage.AboutPrivacy),
            lazy: async () => {
              const { PrivacyPage } = await import("../about/PrivacyPage");
              return { Component: PrivacyPage };
            },
          },
          {
            path: getRelativePath(AppPage.AboutTerms),
            lazy: async () => {
              const { TermsPage } = await import("../about/TermsPage");
              return { Component: TermsPage };
            },
          },
          {
            index: true,
            loader: (x) => {
              // This redirect only works when run in the cloud, in localhost it returns an error.
              const nextPath = getRelativePath(AppPage.AboutFaq);
              const nextSearch = new URL(x.request.url).searchParams.toString();
              const nextLocation = `${nextPath}?${nextSearch}`;
              return redirect(nextLocation);
            },
          },
        ],
      },
      {
        path: "/login/callback",
        lazy: async () => {
          const { default: LoginCallbackPage } = await import(
            "../auth/LoginCallbackPage"
          );
          return { Component: LoginCallbackPage };
        },
        HydrateFallback: StaticLoadingScreen,
        loader: loginCallbackLoader,
      },
      {
        path: "/logout/callback",
        lazy: async () => {
          const { default: LogoutCallbackPage } = await import(
            "../auth/LogoutCallbackPage"
          );
          return { Component: LogoutCallbackPage };
        },
      },
      {
        path: getRelativePath(AppPage.Invitation),
        lazy: async () => {
          const { InvitationLandingPage } = await import(
            "../report/invitation/InvitationLandingPage"
          );
          return { Component: InvitationLandingPage };
        },
      },
      {
        path: getRelativePath(AppPage.Report),
        lazy: async () => {
          const { ReportRoutes } = await import("../report/ReportRoutes");
          return { Component: ReportRoutes };
        },
        loader: reportLoader,
        HydrateFallback: StaticLoadingScreen,
        children: [
          {
            path: getRelativePath(AppPage.ReportLoginCallback),
            lazy: async () => {
              const { ReportLoginCallbackPage } = await import(
                "../report/auth/ReportLoginCallbackPage"
              );
              return { Component: ReportLoginCallbackPage };
            },
          },
          {
            path: getRelativePath(AppPage.ReportForm),
            Component: () => {
              // Todo: Here we should implement a Progress Context like the vehicle contract has
              return <Outlet />;
            },
            children: [
              {
                path: getRelativePath(AppPage.ReportFormAccidentDescription),
                lazy: async () => {
                  const { AccidentDescriptionPage } = await import(
                    "../report/page-one/accidentDescription/AccidentDescriptionPage"
                  );
                  return { Component: AccidentDescriptionPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormVehicle),
                lazy: async () => {
                  const { VehiclePage } = await import(
                    "../report/page-one/VehiclePage"
                  );
                  return { Component: VehiclePage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormDriver),
                lazy: async () => {
                  const { DriverPage } = await import(
                    "../report/page-one/DriverPage"
                  );
                  return { Component: DriverPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormInsurance),
                lazy: async () => {
                  const { InsurancePage } = await import(
                    "../report/page-one/InsurancePage"
                  );
                  return { Component: InsurancePage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormAccident),
                lazy: async () => {
                  const { AccidentPage } = await import(
                    "../report/page-one/AccidentPage"
                  );
                  return { Component: AccidentPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormSketch),
                lazy: async () => {
                  const { SketchPage } = await import(
                    "../report/page-one/sketch/SketchPage"
                  );
                  return { Component: SketchPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormAttachments),
                lazy: async () => {
                  const { AttachmentsPage } = await import(
                    "../report/page-one/AttachmentsPage"
                  );
                  return { Component: AttachmentsPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportFormWitness),
                lazy: async () => {
                  const { WitnessPage } = await import(
                    "../report/page-one/WitnessPage"
                  );
                  return { Component: WitnessPage };
                },
              },
              {
                index: true,
                lazy: async () => {
                  const { ReportLandingPage } = await import(
                    "../report/page-one/ReportLandingPage"
                  );
                  return { Component: ReportLandingPage };
                },
              },
            ],
          },
          {
            path: getRelativePath(AppPage.ReportPreview),
            lazy: async () => {
              const { PreviewPage } = await import(
                "../report/page-one/PreviewPage"
              );
              return { Component: PreviewPage };
            },
          },
          {
            path: getRelativePath(AppPage.ReportStatus),
            lazy: async () => {
              const { SignatureStatusPage } = await import(
                "../report/page-one/SignatureStatusPage"
              );
              return { Component: SignatureStatusPage };
            },
          },
          {
            path: getRelativePath(AppPage.ReportSign),
            children: [
              {
                path: getRelativePath(AppPage.ReportSignCallback),
                lazy: async () => {
                  const { SignCallbackPage } = await import(
                    "../report/page-one/SignCallbackPage"
                  );
                  return { Component: SignCallbackPage };
                },
              },
              {
                index: true,
                lazy: async () => {
                  const { SignPage } = await import(
                    "../report/page-one/SignPage"
                  );
                  return { Component: SignPage };
                },
              },
            ],
          },
          {
            path: getRelativePath(AppPage.ReportConfirmation),
            lazy: async () => {
              const { ConfirmationPage } = await import(
                "../report/page-one/ConfirmationPage"
              );
              return { Component: ConfirmationPage };
            },
          },
          {
            path: PAGE_TWO_PREFIX,
            children: [
              {
                path: getRelativePath(AppPage.ReportPageTwoSupplementalInfo),
                lazy: async () => {
                  const { SupplementalInfoPage } = await import(
                    "../report/page-two/SupplementalInfoPage"
                  );
                  return { Component: SupplementalInfoPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportPageTwoAccidentDescription),
                lazy: async () => {
                  const { AccidentDescriptionPage } = await import(
                    "../report/page-two/AccidentDescriptionPage"
                  );
                  return { Component: AccidentDescriptionPage };
                },
              },
              {
                path: getRelativePath(AppPage.ReportPageTwoInjury),
                lazy: async () => {
                  const { InjureePage } = await import(
                    "../report/page-two/InjureePage"
                  );
                  return { Component: InjureePage };
                },
              },
            ],
          },
          {
            index: true,
            lazy: async () => {
              const { ReportLandingPage } = await import(
                "../report/page-one/ReportLandingPage"
              );
              return { Component: ReportLandingPage };
            },
          },
          { Component: NotFoundPage },
        ],
      },
      { path: "*", Component: NotFoundPage },
      {
        index: true,
        lazy: async () => {
          const { LandingPage } = await import("../report/landing/LandingPage");
          return { Component: LandingPage };
        },
      },
    ],
  },
];
